import { Step, StepLabel, Stepper } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { CiCircleInfo } from "react-icons/ci";
import { FaPhone, FaSearch, FaUser } from "react-icons/fa";
import { showErrorToast, showSuccessToast } from "../../utils/toaster";
import Loader from "../Loader";
import axios from "axios";
import ProductDrawer from "../ProductDrawer";
import { baseurl } from "../../config/url";

function OrderDetails({
  order,
  setselecteditem,
  selecteditem,
  getorders,
  setselectedproduct,
  selectedproduct,
}) {
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [otp, setotp] = useState("");
  const [otpsent, setotpsent] = useState(false);
  const [loader, setloader] = useState(false);

  const extractedDataItemsData =
    order?.tracking &&
    order?.tracking.map((tracking) => {
      const lastStatusUpdate =
        tracking.statusUpdates[tracking.statusUpdates.length - 1];
      const product = order.items.find((item) => {
        if (item?.product?._id === lastStatusUpdate?.productId) {
          return item;
        }
      });
      return {
        status: lastStatusUpdate.status,
        productId: lastStatusUpdate.productId,
        productName: product ? product.product.name : "Unknown",
        productCategory: product ? product.product.category : "Unknown",
        productSubcategory: product ? product?.product?.subcategory : "Unknown",
        productdetail: product.product,
        statusUpdates: tracking.statusUpdates,
        quantity: product?.quantity,
        totalPrice: product?.price,
        ...tracking,
      };
    });

  // ✅ UI-only: nicer stage meta (no logic change)
  const stages = [
    { label: "Pending", color: "warning", description: "Awaiting Confirmation" },
    { label: "Confirmed", color: "info", description: "Order Confirmed" },
    { label: "Shipped", color: "secondary", description: "On the way" },
    { label: "Delivered", color: "success", description: "Successfully Delivered" },
    { label: "Cancelled", color: "error", description: "Order Cancelled" },
  ];

  const currentStatusIndex = stages.findIndex(
    (stage) => stage.label === selecteditem?.status
  );

  const statusTransitions = {
    Pending: ["Confirmed", "Cancelled"],
    Confirmed: ["Shipped", "Cancelled"],
    Shipped: ["Delivered", "Cancelled"],
    OutForDelivery: ["Delivered", "Cancelled"],
    Delivered: [],
  };

  const today = dayjs().format("YYYY-MM-DD");

  const safeOrderId = order?._id || "—";
  const deliveryText = useMemo(() => {
    const dt = selecteditem?.dispatchDetails?.expectedDeliveryDate;
    return dt ? dayjs(dt).format("DD-MMM-YYYY") : "Delivery date not available";
  }, [selecteditem?.dispatchDetails?.expectedDeliveryDate]);

  const fullAddress = useMemo(() => {
    const a = order?.address || {};
    return [
      a.houseNumberOrApartment,
      a.areaOrStreet,
      a.landmark,
      a.cityOrTown,
      a.selectedState,
      a.selectedPlace,
    ]
      .filter(Boolean)
      .join(", ");
  }, [order?.address]);

  let handleChangeStatusClick = async () => {
    if (!selectedStatus) {
      showErrorToast("Please select status to changed!");
      return;
    }
    if (selectedStatus === "Cancelled" && !cancelReason.trim()) {
      showErrorToast("Please provide a reason for cancellation.");
      return;
    }
    setloader(true);
    let body = { status: selectedStatus };
    if (selectedStatus == "Cancelled") body.reason = cancelReason;
    if (selectedStatus == "Delivered") setotpsent(false);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${baseurl}/api/order-tracking/update-ordertracking/${selecteditem._id}`,
        body,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      let message = response?.data?.message;
      showSuccessToast(message || "Tracking status updated");
      if (message == "OTP sent to the customer's email.") {
        setotpsent(true);
      }
      setSelectedStatus("");
      getorders();
      setCancelReason("");
    } catch (error) {
      let message = error?.response?.data?.message;
      showErrorToast(message || "Error updating tracking status");
    } finally {
      setCancelReason("");
      setloader(false);
    }
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleOrderConfirm = async () => {
    if (!expectedDeliveryDate) {
      showErrorToast("Please select the expected delivery date.");
      return;
    }
    setloader(true);
    try {
      const payload = { status: "Confirmed", expectedDeliveryDate };
      await axios.put(
        `${baseurl}/api/order-tracking/update-ordertracking/${selecteditem._id}`,
        payload
      );
      showSuccessToast(
        "Order confirmed successfully! The status has been updated to 'Confirmed'."
      );
      setSelectedStatus("");
      getorders();
    } catch (error) {
      showErrorToast(
        `Error confirming the order: ${
          error.response?.data?.message || error.message
        }`
      );
    } finally {
      setloader(false);
    }
  };

  const verifyOtp = async (orderId) => {
    setloader(true);
    try {
      const response = await axios.post(
        `${baseurl}/api/order-tracking/verify-otp/${orderId}`,
        { otp }
      );
      let message = response?.data?.message;
      if (response.status == 200) {
        showSuccessToast(message || "OTP verified successfully.");
        getorders();
        setotpsent(false);
      } else {
        showErrorToast("OTP verification failed. Please try again.");
      }
    } catch (error) {
      showErrorToast(
        `Error verifying OTP: ${error.response?.data?.message || error.message}`
      );
    } finally {
      setloader(false);
      setotp("");
    }
  };

  useEffect(() => {
    if (extractedDataItemsData && extractedDataItemsData.length > 0) {
      const firstItem = extractedDataItemsData[0];
      setselectedproduct(firstItem.productdetail);
      setselecteditem(firstItem);
    }
  }, [order?._id]);

  return (
    <div className="w-full bg-slate-50 min-h-screen">
      <ProductDrawer
        setOpenDrawer={setOpenDrawer}
        openDrawer={openDrawer}
        selectedProduct={selectedproduct}
      />

      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b">
        <div className="px-4 md:px-6 py-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-extrabold text-slate-900">
              Order Details
            </h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              Order ID: <span className="font-bold text-slate-700">{safeOrderId}</span>
            </p>
          </div>

          {selecteditem?.status && (
            <span className="w-fit inline-flex items-center rounded-full border bg-white px-3 py-1 text-xs font-extrabold text-slate-700">
              Current Status: {selecteditem.status}
            </span>
          )}
        </div>
      </div>

      <div className="px-4 md:px-6 py-5">
        {/* Timeline */}
        <div className="rounded-2xl border bg-white shadow-sm p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-sm font-extrabold text-slate-900">Tracking Timeline</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Status updates from Pending to Delivered / Cancelled
              </p>
            </div>
          </div>

          <div className="overflow-auto">
            <Stepper activeStep={currentStatusIndex} alternativeLabel>
              {stages.map((stage, index) => (
                <Step key={stage.label} completed={index <= currentStatusIndex}>
                  <StepLabel
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontWeight: 800,
                        color: index <= currentStatusIndex ? "#0f172a" : "#64748b",
                      },
                      "& .MuiStepIcon-root": {
                        color: index <= currentStatusIndex ? "#2563eb" : "#cbd5e1",
                      },
                    }}
                  >
                    <div className="text-center">
                      <p className="text-sm">{stage.label}</p>
                      <p className="text-[11px] text-slate-500 font-semibold">
                        {stage.description}
                      </p>
                    </div>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>

          {order?.status === "Cancelled" && (
            <div className="mt-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              Order Cancelled
            </div>
          )}
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
          {/* Left: Order + Customer */}
          <div className="lg:col-span-2 space-y-5">
            {/* Order Information */}
            <div className="rounded-2xl border bg-white shadow-sm p-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-extrabold text-slate-900">Order Information</p>
                <span className="text-xs font-extrabold text-slate-600 border bg-slate-50 px-2.5 py-1 rounded-full">
                  Tracking: {selecteditem?._id || "—"}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">Total Price</p>
                  <p className="text-lg font-extrabold text-slate-900 mt-1">
                    ₹{Number((selecteditem?.totalPrice || 0) * (selecteditem?.quantity || 0)).toFixed(2)}
                  </p>
                </div>

                <div className="rounded-xl border bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">Quantity</p>
                  <p className="text-lg font-extrabold text-slate-900 mt-1">
                    {selecteditem?.quantity ?? "—"}
                  </p>
                </div>

                <div className="rounded-xl border bg-slate-50 p-4 sm:col-span-2">
                  <p className="text-xs font-bold text-slate-500 uppercase">
                    Expected Delivery Date
                  </p>
                  <p className="text-sm font-bold text-slate-900 mt-1">{deliveryText}</p>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="rounded-2xl border bg-white shadow-sm p-5">
              <p className="text-sm font-extrabold text-slate-900">Customer Information</p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                <div className="flex items-center gap-3 rounded-xl border bg-slate-50 p-3">
                  <div className="h-10 w-10 rounded-xl bg-white border grid place-items-center text-slate-600">
                    <FaUser className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-500">Name</p>
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      {order?.user?.name || "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-xl border bg-slate-50 p-3">
                  <div className="h-10 w-10 rounded-xl bg-white border grid place-items-center text-slate-600">
                    <FaSearch className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-500">Email</p>
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      {order?.user?.email || "—"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-xl border bg-slate-50 p-3">
                  <div className="h-10 w-10 rounded-xl bg-white border grid place-items-center text-slate-600">
                    <FaPhone className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-500">Mobile</p>
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      {order?.address?.mobileNumber || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Item List */}
            <div className="rounded-2xl border bg-white shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b">
                <p className="text-sm font-extrabold text-slate-900">Item List</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Click an item to view details and update tracking for that product
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-extrabold text-slate-600">
                      <th className="py-3 px-4">NO</th>
                      <th className="py-3 px-4">ITEM NAME</th>
                      <th className="py-3 px-4">CATEGORY</th>
                      <th className="py-3 px-4">SUBCATEGORY</th>
                      <th className="py-3 px-4">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extractedDataItemsData &&
                      extractedDataItemsData?.map((item, index) => (
                        <tr
                          key={item.productId}
                          className={`text-sm cursor-pointer border-t transition ${
                            selectedproduct?._id === item.productId
                              ? "bg-blue-50"
                              : index % 2 === 0
                              ? "bg-white"
                              : "bg-slate-50/40"
                          } hover:bg-blue-50/60`}
                          onClick={() => {
                            setselectedproduct(item.productdetail);
                            setselecteditem(item);
                          }}
                        >
                          <td className="py-3 px-4 text-slate-700 font-bold">
                            {index + 1}
                          </td>
                          <td className="py-3 px-4">
                            <p className="font-extrabold text-slate-900">
                              {item.productName}
                            </p>
                          </td>
                          <td className="py-3 px-4 text-slate-700 font-semibold">
                            {item?.productCategory?.name}
                          </td>
                          <td className="py-3 px-4 text-slate-700 font-semibold">
                            {item?.productSubcategory?.name}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center rounded-full border bg-white px-3 py-1 text-xs font-extrabold text-slate-700">
                              {item.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Right: Location + Actions */}
          <div className="space-y-5">
            {/* Location + Product */}
            <div className="rounded-2xl border bg-white shadow-sm p-5">
              <p className="text-sm font-extrabold text-slate-900">Location</p>

              <div className="mt-4 space-y-3">
                <div className="rounded-xl border bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">Delivery Location</p>
                  <p className="text-sm font-semibold text-slate-900 mt-1">
                    {fullAddress || "—"}
                  </p>
                </div>

                <div className="rounded-xl border bg-slate-50 p-4">
                  <p className="text-xs font-bold text-slate-500">Product</p>
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setOpenDrawer(true)}
                      className="h-9 w-9 rounded-xl border bg-white grid place-items-center text-slate-700 hover:bg-slate-50 transition"
                      aria-label="View product details"
                    >
                      <CiCircleInfo fontSize={20} />
                    </button>
                    <p className="text-sm font-extrabold text-slate-900 truncate">
                      {selectedproduct?.name || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Actions */}
            {selecteditem && selecteditem.status !== "Pending" && (
              <div className="rounded-2xl border bg-white shadow-sm p-5">
                <p className="text-sm font-extrabold text-slate-900">Update Status</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Allowed transitions based on current status.
                </p>

                <div className="mt-4 space-y-3">
                  <select
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    className="w-full h-11 px-3 border rounded-xl bg-white text-slate-900 font-semibold
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" disabled>
                      Select Status
                    </option>
                    {statusTransitions[selecteditem?.status]?.map((stage) => (
                      <option key={stage} value={stage}>
                        {stage}
                      </option>
                    ))}
                  </select>

                  {selectedStatus === "Cancelled" && (
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-2">
                        Cancellation Reason
                      </label>
                      <input
                        type="text"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        className="w-full h-11 px-3 border rounded-xl bg-white text-slate-900
                                   focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter reason for cancellation"
                      />
                    </div>
                  )}

                  <button
                    onClick={handleChangeStatusClick}
                    className="w-full h-11 rounded-xl bg-blue-600 text-white font-extrabold hover:bg-blue-700 transition
                               flex items-center justify-center"
                  >
                    {loader ? <Loader /> : "Change Status"}
                  </button>
                </div>
              </div>
            )}

            {/* Confirm order (Pending only) */}
            {selecteditem && selecteditem.status === "Pending" && (
              <div className="rounded-2xl border bg-white shadow-sm p-5">
                <p className="text-sm font-extrabold text-slate-900">Confirm Order</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Set an expected delivery date to confirm.
                </p>

                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-2">
                      Estimate Delivery Date
                    </label>
                    <input
                      type="date"
                      value={expectedDeliveryDate}
                      min={today}
                      onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                      className="w-full h-11 px-3 border rounded-xl bg-white text-slate-900
                                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <button
                    onClick={handleOrderConfirm}
                    className="w-full h-11 rounded-xl bg-blue-600 text-white font-extrabold hover:bg-blue-700 transition
                               flex items-center justify-center"
                  >
                    {loader ? <Loader /> : "Confirm Order"}
                  </button>
                </div>
              </div>
            )}

            {/* OTP box */}
            {otpsent && (
              <div className="rounded-2xl border bg-white shadow-sm p-5">
                <p className="text-sm font-extrabold text-slate-900">Verify Delivery OTP</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Enter OTP shared with customer to mark delivered.
                </p>

                <div className="mt-4 space-y-3">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setotp(e.target.value)}
                    placeholder="Enter OTP"
                    className="w-full h-11 px-3 border rounded-xl bg-white text-slate-900
                               focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => verifyOtp(selecteditem?._id)}
                    className="w-full h-11 rounded-xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition
                               flex items-center justify-center"
                  >
                    {loader ? <Loader /> : "Verify OTP"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;